import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, CheckCircle, XCircle, User, Phone, ShoppingBag } from 'lucide-react';

// Placeholder per il componente dashboard
function OrdersDashboard() {
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);

  // Query per recuperare gli ordini
  const { data: orders = [], isLoading, error } = useQuery({
    queryKey: ['orders'],
    queryFn: async () => {
      // TODO: Sostituire con l'URL reale del worker
      const response = await fetch('https://your-worker-url.your-subdomain.workers.dev/api/orders');
      if (!response.ok) throw new Error('Failed to fetch orders');
      return response.json();
    },
    refetchInterval: 5000, // Aggiorna ogni 5 secondi
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        <span className="ml-3 text-lg">Caricamento ordini...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <XCircle className="h-5 w-5 text-red-400" />
          <span className="ml-2 text-red-700">
            Errore nel caricamento degli ordini
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Ordini in Arrivo
          </h2>
          
          {orders.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingBag className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">
                Nessun ordine in attesa al momento
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {orders.map((order: any) => (
                <OrderCard 
                  key={order.id}
                  order={order}
                  onSelect={setSelectedOrder}
                  isSelected={selectedOrder === order.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface OrderCardProps {
  order: any;
  onSelect: (orderId: string) => void;
  isSelected: boolean;
}

function OrderCard({ order, onSelect, isSelected }: OrderCardProps) {
  const queryClient = useQueryClient();

  const confirmMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/orders/${order.id}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pickupTime: new Date(Date.now() + 20 * 60000) }) // 20 minuti
      });
      if (!response.ok) throw new Error('Failed to confirm order');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    }
  });

  const rejectMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/orders/${order.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to reject order');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    }
  });

  return (
    <div 
      className={`border rounded-lg p-4 cursor-pointer transition-all ${
        isSelected 
          ? 'border-orange-500 bg-orange-50' 
          : 'border-gray-200 hover:border-gray-300'
      }`}
      onClick={() => onSelect(order.id)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center">
          <Clock className="h-4 w-4 text-gray-400 mr-1" />
          <span className="text-xs text-gray-500">
            {new Date(order.createdAt).toLocaleTimeString('it-IT')}
          </span>
        </div>
        <span className="text-sm font-bold text-green-600">
          €{order.totalAmount.toFixed(2)}
        </span>
      </div>
      
      <div className="space-y-2 mb-4">
        <div className="flex items-center">
          <User className="h-4 w-4 text-gray-400 mr-2" />
          <span className="text-sm font-medium">{order.customerInfo.name}</span>
        </div>
        <div className="flex items-center">
          <Phone className="h-4 w-4 text-gray-400 mr-2" />
          <span className="text-sm text-gray-600">{order.customerInfo.phone}</span>
        </div>
      </div>
      
      <div className="text-xs text-gray-600 mb-4">
        {order.items.map((item: any, index: number) => (
          <div key={index}>
            {item.quantity}x {item.productName}
            {item.extras && item.extras.length > 0 && (
              <span className="text-gray-500"> + {item.extras.join(', ')}</span>
            )}
          </div>
        ))}
      </div>
      
      <div className="flex space-x-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            confirmMutation.mutate();
          }}
          disabled={confirmMutation.isPending}
          className="flex-1 bg-green-600 text-white px-3 py-2 rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50"
        >
          <CheckCircle className="h-3 w-3 inline mr-1" />
          Accetta
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            rejectMutation.mutate();
          }}
          disabled={rejectMutation.isPending}
          className="flex-1 bg-red-600 text-white px-3 py-2 rounded text-xs font-medium hover:bg-red-700 disabled:opacity-50"
        >
          <XCircle className="h-3 w-3 inline mr-1" />
          Rifiuta
        </button>
      </div>
    </div>
  );
}

export default OrdersDashboard;